import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead from '../../components/SEOHead';

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

const termsJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/termos`,
  name: 'Termos de Uso — EntreNós',
  description:
    'Leia os Termos de Uso do EntreNós: regras de elegibilidade, uso aceitável, planos, pagamentos, propriedade intelectual e mais.',
  url: `${SITE_URL}/termos`,
  inLanguage: 'pt-BR',
  dateModified: '2026-04-09',
  isPartOf: {
    '@type': 'WebSite',
    name: 'EntreNós',
    url: SITE_URL,
  },
  about: {
    '@type': 'Organization',
    name: 'EntreNós',
    url: SITE_URL,
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Termos de Uso', item: `${SITE_URL}/termos` },
    ],
  },
};

export default function TermsPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'aceitacao', label: 'Aceitação dos Termos' },
    { id: 'descricao', label: 'Descrição do Serviço' },
    { id: 'elegibilidade', label: 'Elegibilidade' },
    { id: 'conta', label: 'Sua Conta' },
    { id: 'uso-aceitavel', label: 'Uso Aceitável' },
    { id: 'conteudo', label: 'Conteúdo do Usuário' },
    { id: 'planos', label: 'Planos e Pagamentos' },
    { id: 'propriedade', label: 'Propriedade Intelectual' },
    { id: 'limitacao', label: 'Limitação de Responsabilidade' },
    { id: 'rescisao', label: 'Rescisão' },
    { id: 'alteracoes', label: 'Alterações nos Termos' },
    { id: 'contato', label: 'Contato' },
  ];

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50">
      <SEOHead
        title="Termos de Uso — EntreNós"
        description="Conheça os Termos de Uso do EntreNós: elegibilidade, uso aceitável, planos e pagamentos, propriedade intelectual e limitação de responsabilidade."
        keywords="termos de uso EntreNós, contrato serviço, regras plataforma casais, planos premium"
        canonical="/termos"
        schemaJsonLd={termsJsonLd}
        dateModified="2026-04-09"
      />
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <img
                src="https://static.readdy.ai/image/1b0e966215e432d351b2e9c11ca0b10e/154dcc8a65fed03ee4761cc080c3eddf.png"
                alt="EntreNós"
                fetchPriority="high"
                decoding="async"
                width="120"
                height="40"
                className="h-10 w-auto"
              />
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-gray-100 hover:shadow-md text-sm font-medium"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              <span className="whitespace-nowrap">Voltar ao início</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-rose-200 mb-6">
            <i className="ri-file-text-fill text-rose-500"></i>
            <span className="text-sm font-medium text-gray-700">Leia com atenção antes de usar</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Termos de{' '}
            <span className="bg-gradient-to-r from-rose-500 to-pink-500 bg-clip-text text-transparent">
              Uso
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Ao utilizar o EntreNós, você concorda com os termos e condições descritos abaixo. Leia com atenção.
          </p>
          <p className="text-sm text-gray-400 mt-4">
            Última atualização: 9 de abril de 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-10">

            {/* Sidebar */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-2xl shadow-sm border border-rose-100 p-5">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Índice</p>
                <nav className="space-y-1">
                  {sections.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => scrollTo(s.id)}
                      className="w-full text-left px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-rose-50 hover:text-rose-600 transition-colors cursor-pointer"
                    >
                      {s.label}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main content */}
            <main className="flex-1 space-y-10">

              {/* 1. Aceitação */}
              <div id="aceitacao" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl text-white">
                    <i className="ri-check-double-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">1. Aceitação dos Termos</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    Ao acessar ou utilizar a plataforma <strong className="text-gray-800">EntreNós</strong> — incluindo o site, aplicativo web e todos os serviços associados — você declara ter lido, compreendido e concordado com estes Termos de Uso.
                  </p>
                  <p>
                    Se você não concordar com qualquer parte destes termos, não deverá utilizar nossos serviços. O uso continuado da plataforma após alterações nos termos constitui aceitação das novas condições.
                  </p>
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-information-line text-rose-500 text-lg flex-shrink-0 mt-0.5"></i>
                      <p className="text-sm text-rose-800">
                        Estes Termos de Uso constituem um contrato legal entre você e a <strong>EntreNós Tecnologia Ltda.</strong> Recomendamos salvar ou imprimir uma cópia para referência futura.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Descrição */}
              <div id="descricao" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-pink-500 to-purple-500 rounded-xl text-white">
                    <i className="ri-apps-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">2. Descrição do Serviço</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    O EntreNós é uma plataforma digital voltada ao fortalecimento de relacionamentos amorosos. Nossos serviços incluem:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { icon: 'ri-target-line', color: 'from-rose-500 to-pink-500', title: 'Missões Personalizadas', desc: 'Desafios semanais criados por IA adaptados ao perfil do casal.' },
                      { icon: 'ri-robot-line', color: 'from-pink-500 to-purple-500', title: 'Layla — IA Especialista', desc: 'Consultora virtual treinada em psicologia de casais e comunicação não-violenta.' },
                      { icon: 'ri-gamepad-line', color: 'from-purple-500 to-indigo-500', title: 'Experiências Interativas', desc: 'Jogos, quizzes, desafios e missões criados especialmente para o casal.' },
                      { icon: 'ri-heart-pulse-line', color: 'from-amber-500 to-rose-500', title: 'Modo Reconexão', desc: 'Plano estruturado de 7 dias para reaproximar casais em momentos difíceis.' },
                      { icon: 'ri-trophy-line', color: 'from-rose-500 to-amber-500', title: 'Sistema de Conquistas', desc: 'Pontos, níveis e conquistas para acompanhar a evolução do relacionamento.' },
                      { icon: 'ri-image-line', color: 'from-teal-500 to-rose-500', title: 'Álbum do Casal', desc: 'Espaço para guardar e compartilhar memórias especiais entre os parceiros.' },
                    ].map((item) => (
                      <div key={item.title} className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-8 h-8 flex items-center justify-center bg-gradient-to-br ${item.color} rounded-lg text-white`}>
                            <i className={`${item.icon} text-sm`}></i>
                          </div>
                          <h3 className="font-semibold text-gray-800 text-sm">{item.title}</h3>
                        </div>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-error-warning-line text-amber-600 text-lg flex-shrink-0 mt-0.5"></i>
                      <p className="text-sm text-amber-800">
                        <strong>Aviso importante:</strong> O EntreNós é uma plataforma de apoio e <strong>não substitui terapia profissional, aconselhamento psicológico ou qualquer serviço de saúde mental</strong>. Em situações de crise, procure um profissional habilitado.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. Elegibilidade */}
              <div id="elegibilidade" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-rose-500 rounded-xl text-white">
                    <i className="ri-user-check-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">3. Elegibilidade</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>Para utilizar o EntreNós, você deve atender aos seguintes requisitos:</p>
                  <div className="space-y-3">
                    {[
                      { icon: 'ri-calendar-check-line', color: 'bg-rose-100 text-rose-600', text: 'Ter no mínimo 18 (dezoito) anos de idade.' },
                      { icon: 'ri-global-line', color: 'bg-pink-100 text-pink-600', text: 'Ser residente no Brasil ou em país onde o serviço esteja disponível.' },
                      { icon: 'ri-file-check-line', color: 'bg-purple-100 text-purple-600', text: 'Ter capacidade legal para celebrar contratos vinculantes.' },
                      { icon: 'ri-shield-check-line', color: 'bg-amber-100 text-amber-600', text: 'Não ter sido previamente banido ou suspenso da plataforma.' },
                      { icon: 'ri-lock-line', color: 'bg-green-100 text-green-600', text: 'Fornecer informações verdadeiras, precisas e completas no cadastro.' },
                    ].map((item) => (
                      <div key={item.text} className="flex items-start gap-3 bg-gray-50 rounded-xl px-4 py-3">
                        <div className={`w-8 h-8 flex items-center justify-center ${item.color} rounded-lg flex-shrink-0`}>
                          <i className={`${item.icon} text-sm`}></i>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-error-warning-fill text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
                      <p className="text-sm text-red-800">
                        O uso da plataforma por menores de 18 anos é <strong>estritamente proibido</strong>. Contas identificadas como pertencentes a menores serão encerradas imediatamente.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 4. Conta */}
              <div id="conta" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-rose-500 to-amber-500 rounded-xl text-white">
                    <i className="ri-user-settings-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">4. Sua Conta</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>Ao criar uma conta no EntreNós, você concorda com as seguintes responsabilidades:</p>
                  <div className="space-y-4">
                    <div className="border-l-4 border-rose-400 pl-4">
                      <h3 className="font-semibold text-gray-800 mb-1">Segurança da Conta</h3>
                      <p className="text-sm">Você é responsável por manter a confidencialidade de suas credenciais de acesso (e-mail e senha). Notifique-nos imediatamente em caso de acesso não autorizado.</p>
                    </div>
                    <div className="border-l-4 border-pink-400 pl-4">
                      <h3 className="font-semibold text-gray-800 mb-1">Informações Verdadeiras</h3>
                      <p className="text-sm">Você deve fornecer informações precisas e mantê-las atualizadas. O uso de identidades falsas ou informações enganosas é proibido.</p>
                    </div>
                    <div className="border-l-4 border-purple-400 pl-4">
                      <h3 className="font-semibold text-gray-800 mb-1">Conta Individual</h3>
                      <p className="text-sm">Cada pessoa deve ter apenas uma conta. A criação de múltiplas contas para contornar restrições é proibida.</p>
                    </div>
                    <div className="border-l-4 border-amber-400 pl-4">
                      <h3 className="font-semibold text-gray-800 mb-1">Responsabilidade pelas Ações</h3>
                      <p className="text-sm">Você é responsável por todas as atividades realizadas em sua conta, independentemente de quem as realizou.</p>
                    </div>
                    <div className="border-l-4 border-green-400 pl-4">
                      <h3 className="font-semibold text-gray-800 mb-1">Vínculo com Parceiro(a)</h3>
                      <p className="text-sm">O código de vínculo deve ser compartilhado apenas com seu(sua) parceiro(a) real. O uso indevido do sistema de vínculo pode resultar em suspensão da conta.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 5. Uso Aceitável */}
              <div id="uso-aceitavel" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-green-500 to-teal-500 rounded-xl text-white">
                    <i className="ri-thumb-up-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">5. Uso Aceitável</h2>
                </div>
                <div className="space-y-6 text-gray-600 leading-relaxed">
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <i className="ri-check-line text-green-500"></i> O que você PODE fazer
                    </h3>
                    <div className="space-y-2">
                      {[
                        'Usar a plataforma para fins pessoais e não comerciais.',
                        'Compartilhar conteúdo adequado com seu(sua) parceiro(a) vinculado(a).',
                        'Interagir com a Layla (IA) para obter orientações sobre relacionamentos.',
                        'Completar missões e participar de experiências propostas pela plataforma.',
                        'Personalizar seu perfil com informações verdadeiras.',
                      ].map((item) => (
                        <div key={item} className="flex items-start gap-2 bg-green-50 rounded-lg px-3 py-2">
                          <i className="ri-checkbox-circle-line text-green-500 flex-shrink-0 mt-0.5"></i>
                          <span className="text-sm text-gray-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <i className="ri-close-line text-red-500"></i> O que você NÃO PODE fazer
                    </h3>
                    <div className="space-y-2">
                      {[
                        'Usar a plataforma para assédio, intimidação ou abuso de qualquer pessoa.',
                        'Publicar conteúdo ilegal, ofensivo, discriminatório ou que viole direitos de terceiros.',
                        'Tentar acessar contas de outros usuários sem autorização.',
                        'Realizar engenharia reversa, descompilar ou modificar o software da plataforma.',
                        'Usar bots, scripts ou automações para interagir com a plataforma.',
                        'Revender, sublicenciar ou explorar comercialmente os serviços sem autorização.',
                        'Compartilhar conteúdo sexual explícito fora das funcionalidades designadas para maiores de 18 anos.',
                        'Usar a plataforma para fins fraudulentos ou ilegais.',
                      ].map((item) => (
                        <div key={item} className="flex items-start gap-2 bg-red-50 rounded-lg px-3 py-2">
                          <i className="ri-close-circle-line text-red-500 flex-shrink-0 mt-0.5"></i>
                          <span className="text-sm text-gray-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* 6. Conteúdo */}
              <div id="conteudo" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl text-white">
                    <i className="ri-image-edit-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">6. Conteúdo do Usuário</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    Ao enviar fotos, textos, comentários ou qualquer outro conteúdo para a plataforma, você declara e garante que:
                  </p>
                  <div className="space-y-3">
                    {[
                      { icon: 'ri-copyright-line', text: 'Possui todos os direitos necessários sobre o conteúdo enviado ou tem permissão para usá-lo.' },
                      { icon: 'ri-shield-user-line', text: 'O conteúdo não viola direitos de privacidade, imagem ou propriedade intelectual de terceiros.' },
                      { icon: 'ri-file-forbid-line', text: 'O conteúdo não é ilegal, difamatório, obsceno ou prejudicial a menores.' },
                      { icon: 'ri-lock-unlock-line', text: 'Você concede ao EntreNós uma licença limitada para armazenar e exibir o conteúdo exclusivamente para a prestação do serviço.' },
                    ].map((item) => (
                      <div key={item.text} className="flex items-start gap-3 bg-purple-50 rounded-xl px-4 py-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-purple-100 rounded-lg flex-shrink-0">
                          <i className={`${item.icon} text-purple-600 text-sm`}></i>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500">
                    O EntreNós reserva-se o direito de remover qualquer conteúdo que viole estes termos, sem aviso prévio.
                  </p>
                </div>
              </div>

              {/* 7. Planos e Pagamentos */}
              <div id="planos" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl text-white">
                    <i className="ri-vip-crown-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">7. Planos e Pagamentos</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <i className="ri-heart-line text-rose-500 text-lg"></i>
                        <h3 className="font-semibold text-gray-800">Plano Gratuito</h3>
                      </div>
                      <p className="text-sm text-gray-600">Acesso às funcionalidades básicas sem custo. Disponível por tempo indeterminado, sujeito a alterações com aviso prévio de 30 dias.</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                      <div className="flex items-center gap-2 mb-3">
                        <i className="ri-vip-crown-fill text-amber-500 text-lg"></i>
                        <h3 className="font-semibold text-gray-800">Plano Premium</h3>
                      </div>
                      <p className="text-sm text-gray-600">Assinatura recorrente com acesso completo a todos os recursos. Cobrado conforme o período escolhido (mensal ou anual).</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="border-l-4 border-amber-400 pl-4">
                      <h3 className="font-semibold text-gray-800 mb-1">Renovação Automática</h3>
                      <p className="text-sm">As assinaturas são renovadas automaticamente ao final de cada período, salvo cancelamento prévio pelo usuário.</p>
                    </div>
                    <div className="border-l-4 border-rose-400 pl-4">
                      <h3 className="font-semibold text-gray-800 mb-1">Cancelamento</h3>
                      <p className="text-sm">Você pode cancelar sua assinatura a qualquer momento nas configurações da conta. O acesso Premium permanece ativo até o fim do período pago.</p>
                    </div>
                    <div className="border-l-4 border-pink-400 pl-4">
                      <h3 className="font-semibold text-gray-800 mb-1">Reembolsos</h3>
                      <p className="text-sm">Reembolsos são avaliados caso a caso. Em caso de cobrança indevida, entre em contato em até 7 dias pelo e-mail <strong className="text-gray-700">suporteentrenos@outlook.com</strong>.</p>
                    </div>
                    <div className="border-l-4 border-purple-400 pl-4">
                      <h3 className="font-semibold text-gray-800 mb-1">Alteração de Preços</h3>
                      <p className="text-sm">Reservamo-nos o direito de alterar os preços com aviso prévio de 30 dias. Assinaturas ativas não serão afetadas até o próximo ciclo de renovação.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 8. Propriedade Intelectual */}
              <div id="propriedade" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl text-white">
                    <i className="ri-copyright-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">8. Propriedade Intelectual</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    Todo o conteúdo da plataforma EntreNós — incluindo, mas não se limitando a, textos, imagens, logotipos, ícones, código-fonte, design, missões geradas por IA e a identidade da Layla — é de propriedade exclusiva da <strong className="text-gray-800">EntreNós Tecnologia Ltda.</strong> e está protegido pelas leis de propriedade intelectual brasileiras e internacionais.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      { icon: 'ri-forbid-2-line', color: 'from-red-500 to-rose-500', title: 'Proibido', desc: 'Copiar, reproduzir ou distribuir qualquer conteúdo da plataforma sem autorização expressa.' },
                      { icon: 'ri-code-s-slash-line', color: 'from-rose-500 to-pink-500', title: 'Código Protegido', desc: 'O código-fonte e algoritmos da plataforma são segredos comerciais protegidos por lei.' },
                      { icon: 'ri-trademark-line', color: 'from-pink-500 to-purple-500', title: 'Marca Registrada', desc: 'O nome "EntreNós" e a identidade visual são marcas registradas e não podem ser usados sem permissão.' },
                    ].map((item) => (
                      <div key={item.title} className="bg-indigo-50 rounded-xl p-4 border border-indigo-100">
                        <div className={`w-10 h-10 flex items-center justify-center bg-gradient-to-br ${item.color} rounded-xl text-white mb-3`}>
                          <i className={`${item.icon} text-lg`}></i>
                        </div>
                        <h3 className="font-semibold text-gray-800 text-sm mb-1">{item.title}</h3>
                        <p className="text-xs text-gray-600">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 9. Limitação de Responsabilidade */}
              <div id="limitacao" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-rose-400 to-pink-400 rounded-xl text-white">
                    <i className="ri-scales-3-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">9. Limitação de Responsabilidade</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    O EntreNós é fornecido <strong className="text-gray-800">"no estado em que se encontra"</strong>, sem garantias expressas ou implícitas de qualquer tipo. Na máxima extensão permitida por lei:
                  </p>
                  <div className="space-y-3">
                    {[
                      'Não garantimos que o serviço será ininterrupto, livre de erros ou completamente seguro.',
                      'Não nos responsabilizamos por danos indiretos, incidentais ou consequentes decorrentes do uso da plataforma.',
                      'Não somos responsáveis pelo resultado de relacionamentos ou decisões tomadas com base nas orientações da Layla (IA).',
                      'Nossa responsabilidade total, em qualquer circunstância, não excederá o valor pago pelo usuário nos últimos 3 meses.',
                      'Não nos responsabilizamos por conteúdo gerado por usuários ou por terceiros vinculados à plataforma.',
                    ].map((item) => (
                      <div key={item} className="flex items-start gap-3 bg-gray-50 rounded-xl px-4 py-3">
                        <i className="ri-alert-line text-amber-500 flex-shrink-0 mt-0.5"></i>
                        <p className="text-sm text-gray-700">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 10. Rescisão */}
              <div id="rescisao" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-red-500 to-rose-500 rounded-xl text-white">
                    <i className="ri-user-unfollow-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">10. Rescisão</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-xl p-5">
                      <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <i className="ri-user-line text-rose-500"></i> Pelo Usuário
                      </h3>
                      <p className="text-sm text-gray-600">Você pode encerrar sua conta a qualquer momento nas configurações. Após a exclusão, seus dados serão removidos conforme nossa Política de Privacidade.</p>
                    </div>
                    <div className="bg-red-50 rounded-xl p-5 border border-red-100">
                      <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <i className="ri-shield-line text-red-500"></i> Pelo EntreNós
                      </h3>
                      <p className="text-sm text-gray-600">Podemos suspender ou encerrar sua conta imediatamente em caso de violação destes Termos, atividade fraudulenta ou comportamento prejudicial a outros usuários.</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">
                    Em caso de encerramento por violação dos termos, não haverá reembolso de valores pagos. As seções sobre Propriedade Intelectual e Limitação de Responsabilidade sobrevivem ao encerramento da conta.
                  </p>
                </div>
              </div>

              {/* 11. Alterações */}
              <div id="alteracoes" className="bg-white rounded-2xl shadow-sm border border-rose-100 p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-teal-500 to-green-500 rounded-xl text-white">
                    <i className="ri-refresh-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">11. Alterações nos Termos</h2>
                </div>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    Reservamo-nos o direito de modificar estes Termos de Uso a qualquer momento. Quando realizarmos alterações relevantes, notificaremos você por:
                  </p>
                  <ul className="space-y-2">
                    {[
                      'E-mail cadastrado na plataforma com antecedência mínima de 15 dias',
                      'Notificação dentro do aplicativo ao fazer login',
                      'Banner de aviso na página inicial',
                    ].map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm bg-teal-50 rounded-lg px-3 py-2">
                        <i className="ri-check-line text-teal-500 flex-shrink-0"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p>
                    O uso continuado da plataforma após a data de vigência das alterações constitui aceitação dos novos termos. Caso não concorde, você deve encerrar sua conta antes da data de vigência.
                  </p>
                  <p className="text-sm text-gray-500">
                    Estes Termos são regidos pelas leis da República Federativa do Brasil. Fica eleito o foro da Comarca de São Paulo/SP para dirimir quaisquer controvérsias.
                  </p>
                </div>
              </div>

              {/* 12. Contato */}
              <div id="contato" className="bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl p-8 text-white">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h2 className="text-2xl font-bold">12. Fale Conosco</h2>
                </div>
                <div className="space-y-4">
                  <p className="text-white/90 leading-relaxed">
                    Se você tiver dúvidas, solicitações ou preocupações sobre estes Termos de Uso, entre em contato com nossa equipe:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { icon: 'ri-mail-line', label: 'E-mail Geral', value: 'suporteentrenos@outlook.com' },
                      { icon: 'ri-shield-line', label: 'Privacidade / DPO', value: 'suporteentrenos@outlook.com' },
                      { icon: 'ri-building-line', label: 'Empresa', value: 'EntreNós Tecnologia Ltda.' },
                      { icon: 'ri-time-line', label: 'Prazo de resposta', value: 'Até 5 dias úteis' },
                    ].map((item) => (
                      <div key={item.label} className="bg-white/15 rounded-xl px-4 py-3">
                        <div className="flex items-center gap-2 mb-1">
                          <i className={`${item.icon} text-white/80 text-sm`}></i>
                          <span className="text-xs text-white/70 font-medium uppercase tracking-wide">{item.label}</span>
                        </div>
                        <p className="text-white font-medium text-sm">{item.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-2">
                    <Link
                      to="/privacidade"
                      className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-full text-sm font-medium text-white"
                    >
                      <i className="ri-shield-check-line"></i>
                      Ver Política de Privacidade
                    </Link>
                  </div>
                </div>
              </div>

            </main>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-rose-950 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 text-sm text-rose-300">
            <Link to="/" className="hover:text-white transition-colors">Início</Link>
            <span>·</span>
            <Link to="/privacidade" className="hover:text-white transition-colors">Privacidade</Link>
            <span>·</span>
            <Link to="/termos" className="text-white font-medium">Termos de Uso</Link>
          </div>
          <p className="text-rose-300 text-sm">© 2025 EntreNós. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
